/**
 * Custom hook for auto-saving draft form answers to localStorage
 * and restoring them when the form is reopened.
 *
 * Saves every 5 seconds while the form is being filled.
 * Also handles offline submission persistence and auto-retry.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { SectionType, StatusCheckValue } from '../types';

interface SectionAnswerDraft {
  sectionId: string;
  sectionType: SectionType;
  statusValue?: StatusCheckValue;
  numericValue?: number;
  textValue?: string;
}

interface UseDraftAutoSaveOptions {
  formInstanceId: string | undefined;
  answers: Map<string, SectionAnswerDraft>;
  enabled: boolean;
  onAutoSaveError?: () => void;
}

interface UseDraftAutoSaveResult {
  /** Restores saved draft answers from localStorage */
  restoreDraft: () => Map<string, SectionAnswerDraft> | null;
  /** Clears the saved draft from localStorage */
  clearDraft: () => void;
  /** Persists pending submission to localStorage for offline retry */
  savePendingSubmission: (answers: SectionAnswerDraft[]) => void;
  /** Retrieves pending submission from localStorage */
  getPendingSubmission: () => SectionAnswerDraft[] | null;
  /** Clears pending submission from localStorage */
  clearPendingSubmission: () => void;
  /** Whether there's a pending submission stored locally */
  hasPendingSubmission: boolean;
}

function getDraftKey(formInstanceId: string): string {
  return `operator-form-draft-${formInstanceId}`;
}

function getPendingSubmissionKey(formInstanceId: string): string {
  return `operator-form-pending-${formInstanceId}`;
}

export function useDraftAutoSave({
  formInstanceId,
  answers,
  enabled,
  onAutoSaveError,
}: UseDraftAutoSaveOptions): UseDraftAutoSaveResult {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const answersRef = useRef(answers);
  const [hasPendingSubmission, setHasPendingSubmission] = useState(false);

  // Keep answersRef up to date
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Check for existing pending submission on mount
  useEffect(() => {
    if (formInstanceId) {
      const key = getPendingSubmissionKey(formInstanceId);
      const stored = localStorage.getItem(key);
      setHasPendingSubmission(stored !== null);
    }
  }, [formInstanceId]);

  // Auto-save interval: every 5 seconds
  useEffect(() => {
    if (!enabled || !formInstanceId) return;

    intervalRef.current = setInterval(() => {
      try {
        const currentAnswers = answersRef.current;
        if (currentAnswers.size === 0) return;

        const serializable = Array.from(currentAnswers.entries());
        localStorage.setItem(getDraftKey(formInstanceId), JSON.stringify(serializable));
      } catch {
        onAutoSaveError?.();
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, formInstanceId, onAutoSaveError]);

  const restoreDraft = useCallback((): Map<string, SectionAnswerDraft> | null => {
    if (!formInstanceId) return null;

    try {
      const stored = localStorage.getItem(getDraftKey(formInstanceId));
      if (!stored) return null;

      const entries: [string, SectionAnswerDraft][] = JSON.parse(stored);
      return new Map(entries);
    } catch {
      return null;
    }
  }, [formInstanceId]);

  const clearDraft = useCallback(() => {
    if (!formInstanceId) return;
    localStorage.removeItem(getDraftKey(formInstanceId));
  }, [formInstanceId]);

  const savePendingSubmission = useCallback(
    (submissionAnswers: SectionAnswerDraft[]) => {
      if (!formInstanceId) return;
      try {
        localStorage.setItem(
          getPendingSubmissionKey(formInstanceId),
          JSON.stringify(submissionAnswers)
        );
        setHasPendingSubmission(true);
      } catch {
        // Storage full or unavailable — nothing we can do
      }
    },
    [formInstanceId]
  );

  const getPendingSubmission = useCallback((): SectionAnswerDraft[] | null => {
    if (!formInstanceId) return null;
    try {
      const stored = localStorage.getItem(getPendingSubmissionKey(formInstanceId));
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, [formInstanceId]);

  const clearPendingSubmission = useCallback(() => {
    if (!formInstanceId) return;
    localStorage.removeItem(getPendingSubmissionKey(formInstanceId));
    setHasPendingSubmission(false);
  }, [formInstanceId]);

  return {
    restoreDraft,
    clearDraft,
    savePendingSubmission,
    getPendingSubmission,
    clearPendingSubmission,
    hasPendingSubmission,
  };
}
